const REASON_LINES = {
  missing_calendar: "Collect a contact and hand the lead to the operator.",
  calendar_capability_unproven: "Calendar capability is not proven.",
  confirmation_operator_assisted: "Confirmation stays with the operator.",
  model_nlu_not_admitted: "Model NLU is not admitted.",
  automatic_booking_not_admitted: "Automatic booking is not admitted.",
};

export function reasonCode(reason) {
  if (typeof reason === "string" && reason !== "") {
    return reason;
  }
  if (reason && typeof reason.code === "string" && reason.code !== "") {
    return reason.code;
  }
  return "";
}

export function reasonLine(reason) {
  const code = reasonCode(reason);
  if (!code) {
    return "not configured";
  }
  return REASON_LINES[code] || code;
}

export function reportsNoFreeSlotsClosed(mode) {
  return Boolean(mode) && mode.reports_no_free_slots === false;
}

export function derivedModeAccepted(mode) {
  if (!mode || mode.shows_automatic !== false) {
    return null;
  }
  if (!reportsNoFreeSlotsClosed(mode)) {
    return null;
  }
  if (mode.booking_mode !== "operator_assisted") {
    return null;
  }
  if (mode.model_nlu_enabled !== false) {
    return null;
  }
  if (!Array.isArray(mode.reasons)) {
    return null;
  }
  const codes = mode.reasons.map(reasonCode);
  if (
    !codes.includes("model_nlu_not_admitted")
    || !codes.includes("automatic_booking_not_admitted")
  ) {
    return null;
  }
  return mode;
}

export function readinessNotice() {
  return "Readiness is not evaluated.";
}

export function readinessCheckTimeNotice() {
  return "No readiness check time is recorded.";
}

export function readinessExpiryNotice() {
  return "No readiness expiry is recorded.";
}

export function readinessCandidateNotice() {
  return "No readiness candidate hash is recorded.";
}

export function readinessAvailabilityNotice() {
  return "A readiness read does not promise provider availability.";
}

export function readinessChatNotice() {
  return "A readiness read does not move an existing v2 chat to v1.";
}

export function readinessSnapshotNotice() {
  return "A readiness display is a snapshot, not a standing permission.";
}

export function readinessProviderNotice() {
  return "This readiness result does not read provider status.";
}

export function readinessAuditNotice() {
  return "This screen does not record a readiness audit.";
}

export function readinessMissingNotice() {
  return "A missing readiness result is not a successful check.";
}

export function readinessAdmissionNotice() {
  return "This readiness result is not a final admission.";
}

export function readinessRegisterNotice() {
  return "This readiness result is not written to the capability register.";
}

export function readinessDistinctionNotice() {
  return "This readiness result keeps invalid settings, an unavailable dependency, a capability that is not approved, and a runtime permission separate from rollout authorization.";
}

export function readinessBypassNotice() {
  return "This readiness result does not treat operator-assisted mode as a bypass of tenant isolation, KMS, an incomplete privacy restore ledger, or an unacceptable data profile.";
}

export function readinessPauseNotice() {
  return "This readiness result does not turn paused, takeover, or opt-out into automatic mode from an earlier check.";
}

export function readinessOnCallNotice() {
  return "This readiness result does not promise on-call coverage when emergency staffing is absent, and it does not enable emergency dispatch in that case.";
}

export function readinessTemplateNotice() {
  return "This readiness result does not mark a contact template as reviewed.";
}

export function readinessModeNotice() {
  return "This readiness result does not set the booking mode.";
}

export function readinessCapabilityNotice() {
  return "This readiness result does not report a per-capability status.";
}

export function readinessReasonNotice() {
  return "This readiness result does not carry a reason code.";
}

export function readinessEvidenceNotice() {
  return "This readiness result does not cite an evidence reference.";
}

export function readinessVersionNotice() {
  return "This readiness result does not record an evidence version.";
}

export function readinessOwnerNotice() {
  return "This readiness result does not name a required owner action.";
}

export function readinessProfileNotice() {
  return "This readiness result does not treat a missing calendar, channel, confirmation, capacity, or dependency profile as automatic mode.";
}

export function readinessModelNotice() {
  return "This readiness result does not treat the absence of model NLU as a ban on structured confirmation, and it does not enable that model.";
}

export function readinessAssistedNotice() {
  return "This readiness result does not admit operator-assisted mode from a missing calendar or missing automation.";
}

export function readinessRouteNotice() {
  return "This readiness result does not treat a channel route, a contact route, staffing, hours, deadlines, a data profile, or UI copy as permission for operator-assisted mode.";
}

export function readinessStaleNotice() {
  return "This readiness result does not treat pending, mock-only, failed, or expired evidence as an open check.";
}

export function readinessDependencyNotice() {
  return "This readiness result does not treat an unavailable critical dependency as an open check.";
}

export function readinessRegionNotice() {
  return "This readiness result does not treat an incompatible region or retention as an open check.";
}

export function readinessConfirmationNotice() {
  return "This readiness result does not treat a missing template or a missing confirmation as an open check.";
}

export function readinessOperatorNotice() {
  return "This readiness result does not treat a missing operator route as an open check.";
}

export function readinessReleaseNotice() {
  return "This readiness result does not treat a missing release image or commit as an open check.";
}

export function readinessSpecNotice() {
  return "This readiness result does not treat a missing spec, schema, settings, policy, model, or prompt version as an open check.";
}

export function readinessPhaseNotice() {
  return "This readiness result does not treat a missing phase-5 gate as an open check.";
}

export function readinessProofNotice() {
  return "This readiness result does not treat missing capability evidence or expiry as an open check.";
}

export function readinessScopeNotice() {
  return "This readiness result does not treat a missing provider scope or a missing channel scope as an open check.";
}

export function readinessDatabaseNotice() {
  return "This readiness result does not treat missing database transactions, indexes, or majority as an open check.";
}

export function readinessQueueNotice() {
  return "This readiness result does not treat a missing queue, KMS, or storage as an open check.";
}

export function readinessClockNotice() {
  return "This readiness result does not treat a missing clock sync as an open check.";
}

export function readinessTrialNotice() {
  return "This readiness result does not choose a trial or partner branch.";
}

export function readinessDeploymentNotice() {
  return "This readiness result does not request a deployment profile.";
}

export function readinessSettingsNotice() {
  return "This readiness result does not choose a settings version.";
}

export function readinessCreationNotice() {
  return "This readiness result does not obtain a creation request.";
}

export function readinessTenantNotice() {
  return "This readiness result does not obtain a tenant scope.";
}

export function readinessCandidateSnapshotNotice() {
  return "This readiness result does not obtain a candidate settings snapshot.";
}

export function readinessAccountNotice() {
  return "This readiness result does not prepare an account binding.";
}

export function readinessCredentialNotice() {
  return "This readiness result does not prepare a credential binding.";
}

export function readinessOperatorBindingNotice() {
  return "This readiness result does not prepare an operator binding.";
}

export function readinessBindingScopeNotice() {
  return "This readiness result does not place a binding in a scope.";
}

export function readinessAutomationNotice() {
  return "This readiness result does not use automation for a binding.";
}

export function readinessForeignCredentialNotice() {
  return "This readiness result does not use a foreign credential for a preflight.";
}

export function readinessProviderSupportNotice() {
  return "This readiness result does not treat averaged provider support as a preflight.";
}

export function readinessProvisioningNotice() {
  return "This readiness result does not start a provisioning job.";
}

export function readinessSeparateNotice() {
  return "This readiness result does not treat a job as separate.";
}

export function readinessResumableNotice() {
  return "This readiness result does not treat a job as resumable.";
}

export function readinessTransactionNotice() {
  return "This readiness result does not treat a job as outside a transaction.";
}

export function readinessCleanupNotice() {
  return "This readiness result does not treat a job as cleanup.";
}

export function readinessReconciliationNotice() {
  return "This readiness result does not treat a job as reconciliation.";
}

export function readinessEnablementNotice() {
  return "This readiness result does not perform final enablement.";
}

export function readinessBindingReadinessNotice() {
  return "This readiness result does not require binding readiness before enablement.";
}

export function readinessRoofingUserNotice() {
  return "This readiness result does not create a user with bot type roofing.";
}

export function readinessSettingsDocumentNotice() {
  return "This readiness result does not create a settings document with bot type roofing v2.";
}

export function readinessSchemaRefNotice() {
  return "This readiness result does not fix a schema ref.";
}

export function readinessDefaultRefNotice() {
  return "This readiness result does not fix a default ref.";
}

export function readinessCapabilityRefNotice() {
  return "This readiness result does not fix a capability ref.";
}

export function readinessDataRefNotice() {
  return "This readiness result does not fix a data ref.";
}

export function readinessOperatorRefNotice() {
  return "This readiness result does not fix an operator ref.";
}

export function readinessSettingsSeedNotice() {
  return "This readiness result does not call a settings seed.";
}

export function readinessSettingsNormalizerNotice() {
  return "This readiness result does not call a settings normalizer.";
}

export function readinessRoofingV1DocumentNotice() {
  return "This readiness result does not create a roofing v1 document.";
}

export function readinessRoofingV1LeaveNotice() {
  return "This readiness result does not leave a roofing v1 document.";
}

export function readinessExistingSettingsSeedNotice() {
  return "This readiness result does not allow an existing settings seed.";
}

export function readinessTypedReasonNotice() {
  return "This readiness result does not save a typed reason.";
}

export function readinessSaveAuditNotice() {
  return "This readiness result does not save an audit.";
}

export function readinessPartialV2DocumentNotice() {
  return "This readiness result does not create a partial v2 document.";
}

export function readinessFilledV2DocumentNotice() {
  return "This readiness result does not create a filled v2 document.";
}

export function readinessClaimV2UiNotice() {
  return "This readiness result does not claim v2 in the UI.";
}

export function readinessDecisionNotice() {
  return "This readiness result does not fix a decision.";
}

export function readinessUserIdentityNotice() {
  return "This readiness result does not fix a user identity.";
}

export function readinessSettingsIdentityNotice() {
  return "This readiness result does not fix a settings identity.";
}

export function readinessServerDefaultNotice() {
  return "This readiness result does not fix a server default.";
}

export function readinessProfileRefNotice() {
  return "This readiness result does not fix a profile ref.";
}

export function readinessCommandOutcomeNotice() {
  return "This readiness result does not fix a command outcome.";
}

export function readinessAtomicTenantActivationNotice() {
  return "This readiness result does not fix an atomic tenant activation.";
}

export function readinessRevisionExpiryNotice() {
  return "This readiness result does not repeat a revision expiry check.";
}

export function readinessDuplicateDeploymentNotice() {
  return "This readiness result does not return a duplicate deployment.";
}

export function readinessRetriedDeploymentNotice() {
  return "This readiness result does not return a retried deployment.";
}

export function readinessSameChosenResultNotice() {
  return "This readiness result does not return the same chosen result.";
}

export function readinessTwoSettingsNotice() {
  return "This readiness result does not create two settings.";
}

export function readinessTwoUsersNotice() {
  return "This readiness result does not create two users.";
}

export function readinessTwoCreditAllocationsNotice() {
  return "This readiness result does not create two credit allocations.";
}

export function readinessLostCommitNotice() {
  return "This readiness result does not fix a lost commit.";
}

export function readinessUnknownCommitNotice() {
  return "This readiness result does not fix an unknown commit.";
}

export function readinessRequestIdNotice() {
  return "This readiness result does not fix a request ID.";
}

export function readinessV2CommitNotice() {
  return "This readiness result does not fix a v2 commit.";
}

export function readinessV1CreationNotice() {
  return "This readiness result does not repeat a v1 creation.";
}

export function readinessSameIdCandidatePayloadConflictNotice() {
  return "This readiness result does not mark a same ID candidate payload conflict.";
}

export function readinessPurgeDeletionMarkerSeedNotice() {
  return "This readiness result does not restore a deleted tenant by a repeat purge deletion marker seed.";
}

export function readinessMassSwitchTenantsChatsNotice() {
  return "This readiness result does not mass switch existing tenants and chats.";
}

export function readinessChatSchemaRuntimeNotice() {
  return "This readiness result does not later change a chat schema runtime after creation.";
}

export function readinessV2DemoSeedNotice() {
  return "This readiness result does not build a v2 demo seed from defaults validation.";
}

export function readinessSyntheticDemoDataNotice() {
  return "This readiness result does not hold synthetic data in a demo seed.";
}

export function readinessSafeOperatorRouteNotice() {
  return "This readiness result does not hold a safe operator route in a demo seed.";
}

export function readinessProvenSandboxCapabilitiesNotice() {
  return "This readiness result does not hold proven sandbox capabilities in a demo seed.";
}

export function readinessV1NormalizerNotice() {
  return "This readiness result does not use a v1 normalizer in a demo seed.";
}

export function readinessStableVersionIdempotencyNotice() {
  return "This readiness result does not hold stable version idempotency in a demo seed.";
}

export function readinessProductionCredentialsPiiNotice() {
  return "This readiness result does not hold production credentials or PII in a demo seed.";
}

export function readinessSubstituteEvidenceNotice() {
  return "This readiness result does not substitute readiness or evidence in a demo seed.";
}

export function readinessEnableModelNluNotice() {
  return "This readiness result does not enable model nlu in a demo seed.";
}

export function readinessProfileLanguageGateNotice() {
  return "This readiness result does not pass a separate demo profile language gate in a demo seed.";
}

export function readinessConfirmationPathNotice() {
  return "This readiness result does not close a confirmation path in a demo seed.";
}

export function readinessReconcileActiveOperationNotice() {
  return "This readiness result does not reconcile an active demo operation in a demo seed.";
}

export function readinessKeepOperatorOwnedNotice() {
  return "This readiness result does not keep an active demo operation operator owned in a demo seed.";
}

export function readinessResetReseedBeforeNotice() {
  return "This readiness result does not reset or reseed before a demo seed.";
}

export function readinessDeleteEffectClaimNotice() {
  return "This readiness result does not delete an effect or a claim in a demo seed.";
}

export function readinessChatHistoryReplyNotice() {
  return "This readiness result does not treat chat history as a reply in a demo seed.";
}

export function readinessSaveV2WidgetContractNotice() {
  return "This readiness result does not save a v2 widget contract in a demo seed.";
}

export function readinessSendGhlThroughWidgetT0Notice() {
  return "This readiness result does not send ghl through a widget T0 in a demo seed.";
}

export function readinessOmitClientMessageIdNotice() {
  return "This readiness result does not omit a client message id in a demo seed.";
}

export function readinessShowTransitionProcessingNotice() {
  return "This readiness result does not show transition processing in a demo seed.";
}

export function readinessTreatAvailableSilenceOwnedNotice() {
  return "This readiness result does not treat available silence as owned in a demo seed.";
}

export function readinessTreatErrorRetryableNotice() {
  return "This readiness result does not treat an error as retryable in a demo seed.";
}

export function readinessShowEmptyAssistantBubbleNotice() {
  return "This readiness result does not show an empty assistant bubble in a demo seed.";
}

export function readinessTurnProcessingTimeoutFailedBookingNotice() {
  return "This readiness result does not turn a processing timeout into a failed booking in a demo seed.";
}

export function readinessShowOperatorStatusPathNotice() {
  return "This readiness result does not show an operator status path in a demo seed.";
}

export function readinessRepeatClientMessageIdBeforeFirstIntakeConfirmNotice() {
  return "This readiness result does not repeat a client message id before first intake or confirm in a demo seed.";
}

export function readinessKeepNewClientMessageIdForSameActionNotice() {
  return "This readiness result does not keep a new client message id for same action in a demo seed.";
}

export function readinessScopeChatChannelKeyNotice() {
  return "This readiness result does not scope a chat channel key in a demo seed.";
}

export function readinessTokenRotationIdentityNotice() {
  return "This readiness result does not change identity on token rotation in a demo seed.";
}

export function readinessCanonicalContentConflictNotice() {
  return "This readiness result does not mark one canonical content a conflict in a demo seed.";
}

export function readinessDuplicateRefreshReconnectNotice() {
  return "This readiness result does not read a second result on duplicate refresh reconnect in a demo seed.";
}

export function readinessNewExplicitActionIdNotice() {
  return "This readiness result does not issue a new explicit action id in a demo seed.";
}

export function readinessSessionCursorAuthNotice() {
  return "This readiness result does not read session cursor auth on a server in a demo seed.";
}

export function readinessTenantExpiryCsrfNotice() {
  return "This readiness result does not read tenant expiry csrf on a server in a demo seed.";
}

export function readinessRateSizeLimitNotice() {
  return "This readiness result does not read rate size limit on a server in a demo seed.";
}

export function readinessLocalStorageIdentityNotice() {
  return "This readiness result does not read local storage identity in a demo seed.";
}

export function readinessSendProcessorReturnProviderNotice() {
  return "This readiness result does not send processor return or provider in a demo seed.";
}

export function readinessCoverOrderedOutboundSequenceNotice() {
  return "This readiness result does not cover an ordered outbound sequence in a demo seed.";
}

export function readinessCoverCursorOrdinaryReplyNotice() {
  return "This readiness result does not cover a cursor ordinary reply in a demo seed.";
}

export function readinessCoverLateBookingTruthNotice() {
  return "This readiness result does not cover a late booking truth in a demo seed.";
}

export function readinessCoverCorrectionOperationStatusNotice() {
  return "This readiness result does not cover a correction operation status in a demo seed.";
}

export function readinessCoverLateRejectedAbortedOperatorOwnedUnchangedBookingNotice() {
  return "This readiness result does not cover a late rejected aborted operator owned unchanged booking in a demo seed.";
}

export function readinessDeduplicateOutboundIdNotice() {
  return "This readiness result does not deduplicate by outbound id in a demo seed.";
}

export function readinessEndObservationForeverAfterCheckingStatusNotice() {
  return "This readiness result does not end observation forever after checking status in a demo seed.";
}

export function readinessCreateFictitiousBookingRevisionForStatusNotice() {
  return "This readiness result does not create a fictitious booking revision for status in a demo seed.";
}

export function readinessReplaceOlderResponseReceiptBookingTruthNotice() {
  return "This readiness result does not replace an older response or receipt booking truth in a demo seed.";
}

export function readinessReceivePublishedDispatcherPayloadNotice() {
  return "This readiness result does not receive a published dispatcher payload in a demo seed.";
}

export function readinessSendAuthenticatedReceiptOutboundIdSignedCursorNotice() {
  return "This readiness result does not send an authenticated receipt with an outbound id signed cursor in a demo seed.";
}

export function readinessWaitCommittedAcceptanceNotice() {
  return "This readiness result does not wait for a committed acceptance in a demo seed.";
}

export function readinessCreateAcceptanceFromGetRenderTimerHttpAckInboundNotice() {
  return "This readiness result does not create acceptance from a get render timer http ack inbound in a demo seed.";
}

export function readinessBackdateLateDuplicateReceiptAcceptedAtNotice() {
  return "This readiness result does not backdate a late duplicate receipt accepted-at in a demo seed.";
}

export function readinessDecreaseSequenceReviveStaleOfferNotice() {
  return "This readiness result does not decrease a sequence or revive a stale offer in a demo seed.";
}

export function readinessCountAskCountersFirstAiDisclosureAcceptedReplyNotice() {
  return "This readiness result does not count ask counters or a first ai disclosure on an accepted reply in a demo seed.";
}

export function readinessCountAskCountersFirstAiDisclosureUiRenderSuppressedPayloadNotice() {
  return "This readiness result does not count ask counters or a first ai disclosure on a ui render or a suppressed payload in a demo seed.";
}

export function readinessIncludeActionDescriptorOfferPayloadEnableConfirmCommittedAcceptanceNotice() {
  return "This readiness result does not include an action descriptor in an offer payload or enable confirm after a committed acceptance in a demo seed.";
}

export function readinessEnableConfirmOfferOptionFingerprintRevisionExpiryPermittedModeNotice() {
  return "This readiness result does not enable confirm on an offer option fingerprint revision expiry permitted mode in a demo seed.";
}

export function readinessFirstGetWidgetPayloadRecordReceiptEnableButtonWaitForConfirmBlockReceiptNotice() {
  return "This readiness result does not first get a widget payload record a receipt enable a button wait for confirm block a receipt in a demo seed.";
}

export function readinessReadSecureLinkPageServerStatusAgainLinkOrDomButtonEvidenceNotice() {
  return "This readiness result does not read a secure link page server status again a link or a dom button evidence in a demo seed.";
}

export function readinessSafeGetPreviewOrCreateConsentEffectNotice() {
  return "This readiness result does not a safe get preview or create a consent effect in a demo seed.";
}

export function readinessRunOnlyExplicitPostSessionIdentityNonceCsrfContextTtlConsentCommandNotice() {
  return "This readiness result does not run only an explicit post session identity nonce csrf context ttl consent command in a demo seed.";
}

export function readinessExpiredSupersededForeignAcceptanceUnknownActionNotice() {
  return "This readiness result does not an expired superseded foreign acceptance unknown action in a demo seed.";
}

export function readinessReuseNonceDifferentCommandTypedRefusalNewBookingOperationNotice() {
  return "This readiness result does not reuse a nonce for a different command a typed refusal no new booking operation in a demo seed.";
}

export function readinessIdempotentProcessedPostLostResponseGiveOriginalOutcomeOfferExpiredNotice() {
  return "This readiness result does not an idempotent processed post after a lost response give an original outcome even if an offer expired in a demo seed.";
}

export function readinessNonceConsumptionConsentActionOutcomeDurableTransitionBoundAtomicNotice() {
  return "This readiness result does not a nonce consumption a consent action outcome a durable transition bound atomic in a demo seed.";
}

export function readinessReinterpretCommittedOfferNotAcceptedLateReceiptNotice() {
  return "This readiness result does not reinterpret a committed offer not accepted after a late receipt in a demo seed.";
}

export function readinessNewExplicitConfirmationNewActionMessageIdNotice() {
  return "This readiness result does not a new explicit confirmation a new action message id in a demo seed.";
}

export function readinessEndRecoverySamePermittedEffectNotice() {
  return "This readiness result does not end recovery of a same permitted effect in a demo seed.";
}

export function readinessAfterPostUiReadsAuthoritativeConsentOperationResultNotice() {
  return "This readiness result does not after a post a ui reads an authoritative consent operation result in a demo seed.";
}

export function readinessQueuedTimeoutShowRecordedNotice() {
  return "This readiness result does not a queued timeout show recorded in a demo seed.";
}

export function readinessValidConsentMissingContactOrPhotoStayUnconsumedNoOperationNotice() {
  return "This readiness result does not a valid consent missing contact or photo stay unconsumed no operation in a demo seed.";
}

export function readinessShowBlockedInputNeededFieldNotice() {
  return "This readiness result does not show a blocked input a needed field in a demo seed.";
}

export function readinessVerifiedContactOrMediaProgressContinueSameConsentNotice() {
  return "This readiness result does not a verified contact or media progress continue a same consent in a demo seed.";
}

export function readinessBackendResolverOriginalOfferTtlNotice() {
  return "This readiness result does not via a backend resolver in an original offer ttl in a demo seed.";
}

export function readinessNoNewYesIdenticalFingerprintFreshGatesNotice() {
  return "This readiness result does not no new yes just an identical fingerprint fresh gates in a demo seed.";
}

export function readinessDifferentPersonMediumMaterialFactsNewOfferNotice() {
  return "This readiness result does not a different person or a different medium or material facts need a new offer in a demo seed.";
}

export function readinessNetworkLossClientDenialNotice() {
  return "This readiness result does not a network loss not a client denial in a demo seed.";
}

export function readinessCheckingStatusDifferVerifiedTruthNotice() {
  return "This readiness result does not a checking status differ a verified truth in a demo seed.";
}

export function readinessMobileLayoutKeyboardFocusNotice() {
  return "This readiness result does not a mobile layout a keyboard focus in a demo seed.";
}

export function readinessAccessibleLabelsStatusAnnouncementsNotice() {
  return "This readiness result does not an accessible labels status announcements in a demo seed.";
}

export function readinessRetryReconnectPassBrowserChecksNotice() {
  return "This readiness result does not a retry reconnect pass browser checks in a demo seed.";
}

export function readinessSecureLinkBearerNonceContainPiiNotice() {
  return "This readiness result does not a secure link bearer nonce contain pii in a demo seed.";
}

export function readinessUrlAnalyticsReferrerAccessLogsNotice() {
  return "This readiness result does not a url analytics referrer access logs in a demo seed.";
}

export function readinessErrorReportingBrowserReplayThirdPartyPageResourcesNotice() {
  return "This readiness result does not an error reporting browser replay third party page resources in a demo seed.";
}

export function readinessRedirectPageFlowRedactedBackendTraceNotice() {
  return "This readiness result does not a whole redirect page flow not a redacted backend trace in a demo seed.";
}

export function readinessGetPreviewPrefetchConsumeConsentNonceNotice() {
  return "This readiness result does not a get preview prefetch consume a consent nonce in a demo seed.";
}

export function readinessSeparateExplicitPostKeepsAuthCsrfTtlNotice() {
  return "This readiness result does not a separate explicit post keeps an auth csrf ttl in a demo seed.";
}

export function readinessPendingLines() {
  return [
    "widget confirmation: pending",
    "operator coverage: pending",
    "lifecycle boundary: pending",
    "rollout authorization: pending",
  ];
}

const READINESS_INPUTS = [
  ["widget confirmation", "pending"],
  ["operator coverage", "pending"],
  ["lifecycle boundary", "pending"],
  ["rollout authorization", "pending"],
];

function readinessRecordKeys(value, expected) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }
  const keys = Object.keys(value);
  return keys.length === expected.length && expected.every((key) => keys.includes(key));
}

export function readinessLinesFromResult(body) {
  if (!readinessRecordKeys(body, ["status", "checked_at", "expires_at", "candidate_hash", "inputs"])) {
    return null;
  }
  if (
    body.status !== "not_evaluated"
    || body.checked_at !== null
    || body.expires_at !== null
    || body.candidate_hash !== null
    || !Array.isArray(body.inputs)
    || body.inputs.length !== READINESS_INPUTS.length
  ) {
    return null;
  }
  const lines = [];
  for (let index = 0; index < READINESS_INPUTS.length; index += 1) {
    const item = body.inputs[index];
    const [name, status] = READINESS_INPUTS[index];
    if (!readinessRecordKeys(item, ["name", "status"])) {
      return null;
    }
    if (item.name !== name || item.status !== status) {
      return null;
    }
    lines.push(`${name}: ${status}`);
  }
  const encoded = JSON.stringify(body);
  if (encoded.includes("ready") || encoded.includes("phase_5_accepted")) {
    return null;
  }
  return lines;
}

export function nextReadinessLines(previous, settingsWrite, body) {
  if (settingsWrite === "accepted") {
    return readinessLinesFromResult(body);
  }
  return previous;
}
